import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export type NamedVariantPreviewLine = {
  material_code: string;
  material_name: string;
  quantity: number;
  slot_label: string;
  unit_cost: number;
  extended_cost: number;
  is_changed?: boolean;
  replaced_material_code?: string;
  replaced_material_name?: string;
};

export type NamedVariantPreviewModalModel = {
  applicationId: string;
  title: string;
  lines: NamedVariantPreviewLine[];
  warnings: string[];
};

function replacedSubtext(ln: NamedVariantPreviewLine): string | null {
  const code = (ln.replaced_material_code || "").trim();
  const name = (ln.replaced_material_name || "").trim();
  if (!code && !name) return null;
  if (code && name) return `Replaces ${code} — ${name}`;
  return `Replaces ${code || name}`;
}

export function ManufacturingNamedVariantPreviewModal({
  model,
  busy,
  error,
  onClose,
}: {
  model: NamedVariantPreviewModalModel | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
}) {
  if (!model && !busy && !error) return null;

  const visible = model?.lines.filter((ln) => ln.quantity !== 0) ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
              Preview application BOM
            </h3>
            {model ? (
              <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">{model.title}</p>
            ) : null}
          </div>
          <button type="button" className={ghostBtnClass} onClick={onClose}>
            Close
          </button>
        </div>

        {busy ? (
          <div className="mt-4 text-sm text-gray-500 dark:text-zinc-400">Loading preview…</div>
        ) : null}
        {error ? (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/35 dark:text-red-200">
            {error}
          </div>
        ) : null}

        {model && !busy ? (
          <>
            {model.warnings.length > 0 ? (
              <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-amber-800 dark:text-amber-200">
                {model.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            ) : null}
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-gray-500 dark:text-zinc-400">
                  <tr>
                    <th className="py-1 pr-2">Qty</th>
                    <th className="py-1 pr-2">Material</th>
                    <th className="py-1 pr-2">Slot</th>
                    <th className="py-1 pr-2 text-right">Ext $</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((ln, idx) => {
                    const changed = Boolean(ln.is_changed);
                    const replaced = replacedSubtext(ln);
                    return (
                      <tr
                        key={`${ln.material_code}-${idx}`}
                        className={
                          changed
                            ? "bg-emerald-50/50 outline outline-2 outline-emerald-500 dark:bg-emerald-950/25 dark:outline-emerald-500"
                            : "border-t border-gray-100 dark:border-zinc-800"
                        }
                      >
                        <td className="px-2 py-2 pr-2 align-top tabular-nums">{ln.quantity}</td>
                        <td className="px-2 py-2 pr-2 align-top">
                          <div>
                            <span className="font-medium">{ln.material_code || "?"}</span>{" "}
                            <span className="text-gray-600 dark:text-zinc-300">
                              {ln.material_name}
                            </span>
                          </div>
                          {replaced ? (
                            <div className="mt-0.5 text-xs text-gray-400 dark:text-zinc-500">
                              {replaced}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-2 py-2 pr-2 align-top text-xs text-gray-500">
                          {ln.slot_label || "—"}
                        </td>
                        <td className="px-2 py-2 text-right align-top tabular-nums">
                          {Number.isFinite(ln.extended_cost)
                            ? ln.extended_cost.toFixed(2)
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {visible.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-500">No lines in preview.</div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
